import { UNIT_TYPE_LABELS, UNIT_TYPES } from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  cn,
  Label,
  Modal,
  Select,
  Spinner,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  LOAD_MORE_STEP,
  LoadMore,
  PageHeader,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { WhatsappConnect } from '../components/whatsapp-connect';
import { useCreateUnit, useDeleteUnit, useUnits, useUpdateUnit } from '../hooks';
import type { Unit } from '../types';

const FORM_ID = 'unit-form';

const unitFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(120),
  type: z.enum(UNIT_TYPES),
  phone: z.string().max(40),
  city: z.string().max(80),
  address: z.string().max(200),
  isActive: z.boolean(),
});

type UnitFormValues = z.infer<typeof unitFormSchema>;

const EMPTY: UnitFormValues = {
  name: '',
  type: 'BRANCH',
  phone: '',
  city: '',
  address: '',
  isActive: true,
};

export function UnitsPage() {
  useDocumentTitle('Unidades');

  const canWrite = useCan('settings', 'WRITE');
  const units = useUnits();
  const createUnit = useCreateUnit();
  const updateUnit = useUpdateUnit();
  const deleteUnit = useDeleteUnit();

  const [editing, setEditing] = useState<Unit | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  /** Duas abas na edicao: o cadastro e o WhatsApp daquela unidade. */
  const [aba, setAba] = useState<'dados' | 'whatsapp'>('dados');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UnitFormValues>({ resolver: zodResolver(unitFormSchema), defaultValues: EMPTY });

  const mutationError = createUnit.error ?? updateUnit.error ?? deleteUnit.error;

  function openForm(unit: Unit | null) {
    setEditing(unit);
    setIsFormOpen(true);
    setAba('dados');
    reset(
      unit
        ? {
            name: unit.name,
            type: unit.type,
            phone: unit.phone ?? '',
            city: unit.city ?? '',
            address: unit.address ?? '',
            isActive: unit.isActive,
          }
        : EMPTY,
    );
  }

  function closeForm() {
    setIsFormOpen(false);
    setEditing(null);
    reset(EMPTY);
  }

  function onSubmit(values: UnitFormValues) {
    if (editing) {
      updateUnit.mutate({ id: editing.id, input: values }, { onSuccess: closeForm });
      return;
    }

    createUnit.mutate(values, { onSuccess: closeForm });
  }

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);
  /**
   * A lista vem inteira do servidor; a tela mostra um lote por vez para uma
   * rede com dezenas de filiais nao virar uma coluna sem fim.
   */
  const [visiveis, setVisiveis] = useState(LOAD_MORE_STEP);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<MapPin />}
        title="Unidades"
        description="Matriz e filiais da empresa. Usuarios e cadastros se ligam a elas."
        actions={
          canWrite ? (
            <Button onClick={() => openForm(null)}>
              <Plus className="h-4 w-4" />
              <span>
                Nova<span className="hidden sm:inline"> unidade</span>
              </span>
            </Button>
          ) : null
        }
      />

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      <Modal
        open={isFormOpen}
        onClose={closeForm}
        title={editing ? 'Editar unidade' : 'Nova unidade'}
        size="medium"
        // Excluir mora no proprio cadastro: e o que dispensa a fileira de
        // botoes em cada linha da lista no celular.
        actions={[
          editing
            ? {
                label: 'Excluir',
                icon: <Trash2 />,
                variant: 'ghost' as const,
                disabled: deleteUnit.isPending,
                onClick: () => {
                  setPendingRemoval({ id: editing.id, name: editing.name });
                  closeForm();
                },
              }
            : { label: 'Cancelar', variant: 'ghost' as const, onClick: closeForm },
          ...(aba === 'dados'
            ? [
                {
                  label: editing ? 'Salvar' : 'Criar',
                  type: 'submit' as const,
                  formId: FORM_ID,
                  loading: createUnit.isPending || updateUnit.isPending,
                },
              ]
            : []),
        ]}
      >
        {/* Abas so na edicao: a unidade precisa existir antes de ter numero. */}
        {editing ? (
          <div className="mb-4 flex gap-1 border-b border-border">
            {(
              [
                { key: 'dados', label: 'Dados' },
                { key: 'whatsapp', label: 'WhatsApp' },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                aria-current={aba === item.key}
                onClick={() => setAba(item.key)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  aba === item.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        {editing && aba === 'whatsapp' ? (
          <WhatsappConnect unit={editing} />
        ) : (
          <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormField label="Nome" error={errors.name?.message} {...register('name')} />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select id="type" {...register('type')}>
                  {UNIT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {UNIT_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              </div>

              <FormField
                label="Telefone"
                type="tel"
                error={errors.phone?.message}
                {...register('phone')}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Cidade" error={errors.city?.message} {...register('city')} />
              <FormField
                label="Endereco"
                error={errors.address?.message}
                {...register('address')}
              />
            </div>

            <Checkbox label="Unidade ativa" {...register('isActive')} />
          </form>
        )}
      </Modal>

      <Card variant="flush">
        <CardContent>
          {units.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : units.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(units.error)}</p>
          ) : units.data && units.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {units.data.data.slice(0, visiveis).map((unit) => (
                <li
                  key={unit.id}
                  // Acoes a direita, na mesma linha do nome: empilhadas, cada
                  // unidade gastava duas alturas no celular.
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0"
                >
                  {/* A linha toda abre o cadastro: no celular o alvo e o
                      registro inteiro, nao um icone no canto. */}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openForm(unit)}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{unit.name}</span>
                      <Badge variant={unit.type === 'HEAD_OFFICE' ? 'default' : 'secondary'}>
                        {UNIT_TYPE_LABELS[unit.type]}
                      </Badge>
                      {unit.isActive ? null : <Badge variant="destructive">Inativa</Badge>}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[unit.city, unit.address, unit.phone].filter(Boolean).join(' - ') ||
                        'Sem endereco cadastrado'}
                    </span>
                  </button>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => openForm(unit)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        disabled={deleteUnit.isPending}
                        onClick={() => setPendingRemoval({ id: unit.id, name: unit.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<MapPin />} title="Nenhuma unidade cadastrada" />
          )}

          <LoadMore
            meta={
              units.data
                ? {
                    page: 1,
                    perPage: visiveis,
                    total: units.data.data.length,
                    totalPages: Math.ceil(units.data.data.length / visiveis),
                  }
                : undefined
            }
            label="unidades"
            onLoadMore={() => setVisiveis((atual) => atual + LOAD_MORE_STEP)}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir unidade"
        message={`A unidade ${pendingRemoval?.name ?? ''} sai da empresa.`}
        loading={deleteUnit.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteUnit.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
